/**
 * Which way the page is lit, and the one place that decides.
 *
 * Dark is the product. The identity is five near-blacks and one green, the
 * terminal is black because a terminal is black, and every screenshot, every
 * cover and every diagram in this repository was drawn against it. Light is
 * an option for the person who needs one — a bright room, a projector, a
 * screen policy, eyes that prefer it — and it is never chosen on anyone's
 * behalf.
 *
 * Which is why `prefers-color-scheme` is deliberately not consulted. A great
 * many people run their operating system light without having any opinion
 * about this page, and a product whose whole design argument is a dark ramp
 * should not hand half its visitors a different argument because of a
 * checkbox in System Settings. The default is dark for everybody; light is
 * something you ask for, once, and then keep.
 *
 * Deliberately not React state, for the reasons in lib/locale.ts: the
 * document's `data-theme` has to be set before React exists (index.html does
 * the first half of that, and this module is what makes it durable), and a
 * module with a subscriber list serves the components and the document both.
 */

export const THEMES = ['dark', 'light'] as const;

export type Theme = (typeof THEMES)[number];

/** The theme everything is designed against, and the one a first visit gets. */
export const DEFAULT_THEME: Theme = 'dark';

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/**
 * Shared with the four lines of `<script>` in index.html that apply the theme
 * before the first paint. Those lines cannot import this module — they run
 * before the bundle — so the key is written twice, and this comment is the
 * other half of the pair. Change one and change the other.
 */
const KEY = 'engineeros.theme';

/**
 * There is a document to paint. False under `node --test`, where everything
 * below degrades to "the default theme, and nothing painted".
 */
const inBrowser = typeof document !== 'undefined' && typeof window !== 'undefined';

/**
 * `?theme=light` in the address bar, for the same reason `?lang=ar` exists:
 * it makes a link shareable — a lecturer putting this on a projector, a bug
 * report about the light theme — and the choice it expresses is then kept
 * where choices are kept rather than threaded onto every link in the product.
 */
function fromUrl(): Theme | null {
  try {
    const asked = new URLSearchParams(window.location.search).get('theme');
    return isTheme(asked) ? asked : null;
  } catch {
    return null;
  }
}

function fromStorage(): Theme | null {
  if (!inBrowser) return null;
  try {
    const saved = window.localStorage.getItem(KEY);
    return isTheme(saved) ? saved : null;
  } catch {
    // Private mode, or storage switched off. Not knowing is survivable: it
    // means the default, which is the theme the product is designed in.
    return null;
  }
}

function detect(): Theme {
  if (!inBrowser) return DEFAULT_THEME;
  return fromUrl() ?? fromStorage() ?? DEFAULT_THEME;
}

let current: Theme = detect();

const listeners = new Set<(theme: Theme) => void>();

/**
 * Put the theme on the document.
 *
 * The attribute is written for both values rather than only for light, even
 * though the dark tokens live on bare `:root` and need no attribute to win.
 * A DOM that says `data-theme="dark"` is answerable — from the devtools, from
 * a screenshot harness, from a bug report — where an absent attribute is
 * ambiguous between "dark" and "this build has no themes".
 *
 * `color-scheme` is the other half, and it is not decoration: it is what
 * tells the browser which way to render a scrollbar, a date picker and the
 * canvas behind an overscroll. Getting it wrong leaves white scrollbars down
 * a black page. The meta tag in index.html is the pre-paint copy of this.
 */
function paint(theme: Theme): void {
  if (!inBrowser) return;
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta) meta.setAttribute('content', theme);
}

// Before React, during module evaluation. index.html has usually applied the
// stored theme already; this is what makes `?theme=` win over it, and what
// corrects the attribute when storage was unreadable at that point.
paint(current);

export function getTheme(): Theme {
  return current;
}

/** True when the page is lit from the other side, for the few places that must ask. */
export function isLight(): boolean {
  return current === 'light';
}

/**
 * Change the theme.
 *
 * Idempotent and quiet when nothing changed, like `setLocale`: this is called
 * from a toggle that can be clicked twice, and repainting the whole tree for
 * nothing is a cost a control that cheap should not carry.
 */
export function setTheme(theme: Theme): void {
  if (theme === current) return;
  current = theme;
  paint(theme);
  try {
    if (inBrowser) window.localStorage.setItem(KEY, theme);
  } catch {
    // The choice still holds for this visit; it just will not outlive it.
  }
  for (const listener of listeners) listener(theme);
}

/** Subscribe to theme changes. Returns the unsubscribe. */
export function onTheme(listener: (theme: Theme) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The other theme, for a control that has exactly two. */
export function otherTheme(theme: Theme = current): Theme {
  return theme === 'dark' ? 'light' : 'dark';
}
